// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, CircularProgress } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Table,
  type UsePaginationResult,
} from '@shared/ui-kit/components/grouped-table';
import { CellData } from '@shared/ui-kit/components/grouped-table/cell-data';
import { ChartContainer } from '@shared/ui-kit/components/charts/chart-container';
import { IconButton } from '@shared/ui-kit/components/icon-button/icon-button';
import { AssetBrand } from '@shared/ui-kit/components/popups/asset-brand/asset-brand';
import { type FC, useCallback, useMemo, useState } from 'react';
import type { FundHoldingDetail } from '@shared/data-access/interfaces/api/ai/commons';
import { useTranslation } from 'react-i18next';

interface AssetProfileTopHoldingsProps {
  holdings?: FundHoldingDetail[];
  pagination: UsePaginationResult;
  loading: boolean;
  showAnalyzeButton: boolean;
  onAnalyzeHoldings?: () => void;
  onDownloadHoldings: () => void;
}

const columnHelper = createColumnHelper<FundHoldingDetail>();

export const AssetProfileTopHoldings: FC<AssetProfileTopHoldingsProps> = ({
  pagination,
  loading,
  holdings = [],
  showAnalyzeButton,
  onAnalyzeHoldings,
  onDownloadHoldings,
}) => {
  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(() => pagination.pageSize === 50);

  const hasNames = useMemo(
    () => holdings.some(({ name }) => !!name && name !== 'missing'),
    [holdings],
  );

  const handleShowMore = useCallback(() => {
    setShowMore(prev => !prev);
    pagination.onPaginationChange({
      pageIndex: 0,
      pageSize: 50,
    });
  }, [pagination]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('ticker', {
        header: t('common.ticker', { ns: 'common' }),
        minSize: 150,
        cell: ({ row }) => {
          const { url_logo, url_morningstar } = row.original;
          return (
            <AssetBrand
              ticker={row.original.ticker}
              assetType="stock"
              logoUrl={url_logo}
              url={url_morningstar}
            />
          );
        },
      }),
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        meta: { initiallyHidden: !hasNames },
        cell: ({ row }) => {
          const { name, ticker } = row.original;
          const value = name === 'missing' ? ticker : name!;
          return (
            <CellData format="text" value={value} oneLine className="uppercase" />
          );
        },
      }),
      columnHelper.accessor('isin', {
        header: t('common.isin', { ns: 'common' }),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('percentage', {
        header: t('common.weight', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data format="percentage" value={getValue() / 100} />
        ),
      }),
    ],
    [hasNames, t],
  );

  const actions = useMemo(() => {
    return (
      !!holdings.length && (
        <div className="flex items-center gap-4">
          {showAnalyzeButton && (
            <IconButton
              data-testid="analysis-button"
              icon="Analysis"
              variant="button"
              onClick={onAnalyzeHoldings}
            />
          )}
          <IconButton
            data-testid="download-button"
            icon="Download"
            variant="button"
            onClick={onDownloadHoldings}
          />
        </div>
      )
    );
  }, [holdings.length, onAnalyzeHoldings, onDownloadHoldings, showAnalyzeButton]);

  return (
    <ChartContainer
      header={t('glossary.top-holdings', { ns: 'glossary' })}
      className="h-[540px] w-[678px]"
      headerRight={actions}
    >
      {loading ? (
        <CircularProgress size={50} className="m-auto" color="primary" />
      ) : (
        <div className="flex flex-col gap-4 overflow-auto">
          <Table.Grouped
            columns={columns}
            data={holdings}
            enablePagination={showMore}
            manualPagination
            pageCount={pagination.pageCount}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={pagination.onPaginationChange}
          />
          {!showMore && (
            <Button
              className="self-center"
              color="secondary"
              onClick={handleShowMore}
            >
              {t('common.show-more', { ns: 'common' })}
            </Button>
          )}
        </div>
      )}
    </ChartContainer>
  );
};
